import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { getArticleBySlug, articles } from '../data/articles';
import { Clock, ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';

const ArtikelDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticleBySlug(slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | MEHRAZAV Ratgeber`;
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', article.metaDescription);
      }
    }
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-white mb-4">Artikel nicht gefunden</h1>
          <Link to="/ratgeber">
            <Button className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-6 py-4">
              Zurück zum Ratgeber
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Find next and previous articles
  const currentIndex = articles.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="artikel-detail-page">
      <Navbar />
      {/* Hero glow that extends behind navbar */}
      <div className="hero-glow" />
      <main className="pt-20 relative">
        {/* Header */}
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
            {/* Back Link */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <Link 
                to="/ratgeber" 
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <ArrowLeft size={16} />
                <span>Zurück zum Ratgeber</span>
              </Link>
            </motion.div>

            {/* Article Meta */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center gap-4 mb-6"
            >
              <span className="text-xs uppercase tracking-wider text-primary font-semibold px-3 py-1 bg-primary/10 rounded-full">
                {article.category}
              </span>
              <div className="flex items-center gap-1 text-muted-foreground text-sm">
                <Clock size={14} />
                <span>{article.readTime} Lesezeit</span>
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold tracking-tight leading-tight mb-6"
              data-testid="article-title"
            >
              {article.title}
            </motion.h1>

            {/* Intro */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-lg text-muted-foreground leading-relaxed"
            >
              {article.content.intro}
            </motion.p>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-12 md:py-16 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="space-y-12"
            >
              {/* Content Sections */}
              {article.content.sections.map((section, index) => (
                <div key={index} className="space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-white">
                    {section.title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}

              {/* Consulting Section */}
              <div className="bg-card/50 border border-primary/20 rounded-2xl p-8 mt-12">
                <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                  {article.content.consultingSection.title}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {article.content.consultingSection.content}
                </p>
              </div>

              {/* CTA Box */}
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-3">
                  Haben Sie konkrete Fragen?
                </h3>
                <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                  Lassen Sie uns über Ihre Situation sprechen. Sie erhalten eine klare Einschätzung und einen konkreten Fahrplan.
                </p>
                <Link to="/kontakt">
                  <Button 
                    className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                  >
                    Kostenlose Ersteinschätzung vereinbaren
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Navigation to other articles */}
        <section className="py-12 md:py-16 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {prevArticle && (
                <Link 
                  to={`/ratgeber/${prevArticle.slug}`}
                  className="group bg-card/30 border border-white/10 rounded-2xl p-6 hover:border-primary/30 transition-all"
                >
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                    <ArrowLeft size={14} />
                    <span>Vorheriger Artikel</span>
                  </div>
                  <h3 className="font-heading font-semibold text-white group-hover:text-primary transition-colors">
                    {prevArticle.title}
                  </h3>
                </Link>
              )}
              {nextArticle && (
                <Link 
                  to={`/ratgeber/${nextArticle.slug}`}
                  className="group bg-card/30 border border-white/10 rounded-2xl p-6 hover:border-primary/30 transition-all md:text-right md:ml-auto"
                >
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3 md:justify-end">
                    <span>Nächster Artikel</span>
                    <ArrowRight size={14} />
                  </div>
                  <h3 className="font-heading font-semibold text-white group-hover:text-primary transition-colors">
                    {nextArticle.title}
                  </h3>
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default ArtikelDetail;
